import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  addItemToCart,
  clearCart,
  getUserCart,
  removeItemFromCart,
  updateCartItemQuantity,
} from "../../../services/cart";

import useOrders from "../orders/useOrders";
import { useAuth } from "../../../utils/AuthContext";

function useCart() {
  const queryClient = useQueryClient();
  const { isLoggedIn } = useAuth();
  const [updatingItemId, setUpdatingItemId] = useState(null);
  const [removingItemId, setRemovingItemId] = useState(null);
  const { orderSuccess } = useOrders();

  // Get Cart Query - only enabled when user is logged in and on cart page
  const {
    data: cartItems,
    isLoading: isGettingCart,
    isError: isErrCart,
  } = useQuery({
    queryKey: ["usercartItems"],
    queryFn: getUserCart,
    enabled: isLoggedIn,
  });

  // Add to Cart Mutation
  const { mutate: addItemToCartFn, isLoading: isAddingItem } = useMutation({
    mutationFn: ({ productId, quantity }) => addItemToCart(productId, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries("cart");
      toast.success("Item added successfully");
    },
    onError: (error, newItem, context) => {
      // Rollback on error
      queryClient.setQueryData("cart", context.previousCart);
      toast.error(error.response?.data?.error || "Some error occurred");
    },
  });

  // Remove from Cart Mutation
  const { mutate: removeItemFromCartFn, isLoading: isRemovingProduct } =
    useMutation({
      mutationFn: (productId) => removeItemFromCart(productId),
      onMutate: (productId) => {
        setRemovingItemId(productId);
      },
      onSuccess: () => {
        queryClient.invalidateQueries("cart");
        toast.success("Item removed successfully");
        setRemovingItemId(null);
      },
      onError: (error, productId, context) => {
        queryClient.setQueryData("cart", context.previousCart);
        toast.error(error.response?.data?.error || "Some error occurred");
        setRemovingItemId(null);
      },
    });

  // Update Quantity Mutation
  const { mutate: updateItemQuantityFn, isLoading: isUpdatingQuantity } =
    useMutation({
      mutationFn: ({ productId, quantity }) =>
        updateCartItemQuantity(productId, quantity),
      onMutate: ({ productId }) => {
        setUpdatingItemId(productId);
      },
      onSuccess: () => {
        queryClient.invalidateQueries("cart");
        toast.success("Item updated successfully");
        setUpdatingItemId(null);
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData("cart", context.previousCart);
        toast.error(error.response?.data?.error || "Some error occurred");
        setUpdatingItemId(null);
      },
    });

  // Clear Cart Mutation
  const { mutate: clearCartFn, isLoading: isClearingCart } = useMutation({
    mutationFn: clearCart,
    onSuccess: () => {
      queryClient.invalidateQueries("cart");
      toast.success("Cart cleared successfully");
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData("cart", context.previousCart);
      toast.error(error.response?.data?.error || "Some error occurred");
    },
  });

  return {
    cartItems,
    isGettingCart,
    isErrCart,
    addItemToCartFn,
    isAddingItem,
    removeItemFromCartFn,
    isRemovingProduct,
    removingItemId,
    updateItemQuantityFn,
    isUpdatingQuantity,
    updatingItemId,
    clearCartFn,
    isClearingCart,
    orderSuccess,
  };
}

export default useCart;
